/**
 * Optional AI art for the Quote card, via OpenAI `gpt-image-1`.
 * No React imports.
 *
 * WHY OPENAI AND NOT REPLICATE (see DECISIONS.md): Replicate's
 * `OPTIONS /v1/predictions` returns 200 with **zero** `access-control-*`
 * headers, so it is browser-blocked outright, and its create-then-poll shape
 * would need a polling loop on a permanently pinned tab. OpenAI's images
 * endpoint passes preflight (`ACAO: *`, allows `content-type` and
 * `authorization`) and answers in one request.
 *
 * WHY `b64_json`: the URL form hands back a short-lived object-store link
 * that the page would then have to fetch cross-origin a second time — a
 * second CORS surface, a second failure mode, and a second thing to get
 * wrong. Inlining the bytes means the only network call is this one.
 *
 * FAILURE POLICY, which is the whole contract of this module: it never
 * throws, never rejects, never logs, and never returns a half-usable value.
 * Unset key, 401, 429, moderation refusal, timeout, malformed body, empty
 * payload — all return `null`, and the caller keeps the gradient. The card
 * must never show a broken <img>, a blank panel, or a console error.
 */

import { fetchJson } from '../http';
import type { Quote } from './quotes';

const ENDPOINT = 'https://api.openai.com/v1/images/generations';
const MODEL = 'gpt-image-1';

/** Generation genuinely takes 5–15s; the default 10s timeout would kill it. */
const TIMEOUT_MS = 45_000;

/** Below this, the response is not a plausible image and is discarded. */
const MIN_PAYLOAD_CHARS = 512;

/**
 * The prompt is as much a *layout* instruction as an art direction. The image
 * sits behind live text, so it has to leave the lower half quiet and must not
 * contain lettering of its own — a generated image with words in it fights
 * the quote instead of carrying it.
 *
 * The quote is quoted rather than paraphrased so the art tracks the day's
 * actual text, which is what makes a generated card feel deliberate.
 */
export function buildImagePrompt(quote: Quote): string {
  return [
    'An abstract atmospheric painting to sit behind a short printed quotation.',
    'Soft directional light, painterly texture, a single cohesive muted palette,',
    'and generous empty space across the lower half of the frame.',
    'No text, no lettering, no words, no numbers, no people, no faces, no logos, no borders.',
    `Evoke the mood of this line: "${quote.text}"`,
  ].join(' ');
}

interface ImagesResponse {
  readonly data?: ReadonlyArray<{
    readonly b64_json?: unknown;
    readonly output_format?: unknown;
  }>;
}

/** `'png' | 'webp' | 'jpeg'` from the response, defaulting to png. */
function mimeFor(format: unknown): string {
  if (format === 'webp') return 'image/webp';
  if (format === 'jpeg' || format === 'jpg') return 'image/jpeg';
  return 'image/png';
}

/**
 * Generate the day's image.
 *
 * @param apiKey `undefined` is the normal, fully-supported configuration —
 *               it short-circuits before any network call is made.
 * @returns a `data:` URL ready to drop into `<img src>`, or `null`.
 */
export async function generateQuoteImage(
  quote: Quote,
  apiKey: string | undefined,
  signal?: AbortSignal,
): Promise<string | null> {
  if (apiKey === undefined) return null;

  const res = await fetchJson<ImagesResponse>(ENDPOINT, {
    method: 'POST',
    timeoutMs: TIMEOUT_MS,
    ...(signal !== undefined ? { signal } : {}),
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      model: MODEL,
      prompt: buildImagePrompt(quote),
      n: 1,
      size: '1024x1024',
      response_format: 'b64_json',
    }),
  });

  if (!res.ok) return null;

  const first = res.data.data?.[0];
  const b64 = first?.b64_json;
  if (typeof b64 !== 'string' || b64.length < MIN_PAYLOAD_CHARS) return null;

  return `data:${mimeFor(first?.output_format)};base64,${b64}`;
}

/** Cheap shape check for a value coming back out of localStorage. */
export function isImageDataUrl(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith('data:image/') && value.includes(';base64,');
}
